import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, FileCheck, Lock, Scan, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';

const ProcessingPage = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);

  const steps = [
    { icon: FileCheck, text: 'Uploading document...', color: 'text-trust' },
    { icon: Scan, text: 'Extracting text...', color: 'text-verify' },
    { icon: Lock, text: 'Removing sensitive information...', color: 'text-warning' },
    { icon: Sparkles, text: 'Analyzing document...', color: 'text-trust' },
    { icon: FileCheck, text: 'Preparing results...', color: 'text-verify' },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentStep((prev) => {
        if (prev < steps.length - 1) {
          return prev + 1;
        } else {
          clearInterval(interval);
          setTimeout(() => {
            navigate('/results');
          }, 500);
          return prev;
        }
      });
    }, 1500);

    return () => clearInterval(interval);
  }, [navigate]);

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center px-6">
      {/* Logo */}
      <div className="mb-12">
        <div className="flex items-center gap-2">
          <Shield className="w-8 h-8 text-trust" />
          <span className="font-outfit font-bold text-2xl text-slate-900">Visa-Ready</span>
        </div>
      </div>

      {/* Processing Animation */}
      <div className="w-full max-w-2xl">
        <div className="bg-white rounded-lg border border-slate-200 p-12 shadow-lg" data-testid="processing-container">
          {/* Scanning Animation */}
          <div className="relative mb-12">
            <div className="bg-trust/5 w-32 h-40 mx-auto rounded-lg border-2 border-trust/20 overflow-hidden relative">
              <motion.div
                className="absolute inset-x-0 h-1 bg-gradient-to-r from-transparent via-trust to-transparent"
                animate={{ y: [0, 160, 0] }}
                transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <FileCheck className="w-12 h-12 text-trust/30" />
              </div>
            </div>
          </div>

          {/* Steps */}
          <div className="space-y-4">
            {steps.map((step, idx) => {
              const isActive = idx === currentStep;
              const isCompleted = idx < currentStep;

              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ 
                    opacity: isActive || isCompleted ? 1 : 0.3,
                    x: 0 
                  }}
                  className="flex items-center gap-4"
                  data-testid={`processing-step-${idx}`}
                >
                  <div className={`${
                    isCompleted 
                      ? 'bg-verify text-verify-foreground' 
                      : isActive 
                      ? 'bg-trust text-trust-foreground animate-pulse-slow' 
                      : 'bg-slate-200 text-slate-400'
                  } w-10 h-10 rounded-full flex items-center justify-center transition-all`}>
                    <step.icon className="w-5 h-5" />
                  </div>
                  <span className={`font-public font-medium ${
                    isActive ? 'text-slate-900' : isCompleted ? 'text-slate-600' : 'text-slate-400'
                  }`}>
                    {step.text}
                  </span>
                </motion.div>
              );
            })}
          </div>

          {/* Privacy Message */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 }}
            className="mt-8 pt-8 border-t border-slate-200"
          >
            <div className="flex items-center justify-center gap-2 text-verify">
              <Lock className="w-4 h-4" />
              <span className="font-public text-sm font-medium">Your document is being processed securely</span>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ProcessingPage;
